import React from 'react';
import PropTypes from 'prop-types';
import { ThemeConsumer } from '@cvent/carina-theme-provider';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { carinaFontReset } from './styleHelper';

/**
 * CheckBox control with no surrounding label and error/validation message
 * functionality for React.
 */
export default class CheckBox extends React.Component {
  static propTypes = {
    /** HTML id attribute will be applied as appropriate */
    id: PropTypes.string.isRequired,
    /** aria-label or accessibilityLabel attribute */
    accessibilityLabel: PropTypes.string,
    /** Input will be displayed as checked */
    checked: PropTypes.bool,
    /** Checkbox instance label */
    children: PropTypes.node,
    /** Reference a validation message element via the aria-describedby attribute */
    describedBy: PropTypes.string,
    /** Input will be displayed in the disabled state disallowing user input */
    disabled: PropTypes.bool,
    /** Will auto-focus the input on component mount */
    focused: PropTypes.bool,
    /** Prop for altering input display when a validation or other error occurs. */
    hasError: PropTypes.bool,
    /** indeterminate visual state. Checkbox is not `checked` for form submission purposes. */
    indeterminate: PropTypes.bool,
    /** Text direction */
    isRtl: PropTypes.bool,
    /** HTML name attribute for input */
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    /** required prop will set accessibility-related input attributes */
    required: PropTypes.bool,
    /** For e2e tests (maps to `data-cvent-id`) */
    testID: PropTypes.string,
    /** CheckBox is a controlled input but does not control its own state.
     *  The user must control the input value via this prop using its own
     *  state mechanism.
     */
    value: PropTypes.string
  };

  static defaultProps = {
    disabled: false,
    focused: false,
    hasError: false,
    indeterminate: false,
    isRtl: false
  };

  state = { isFocused: this.props.focused && !this.props.disabled };

  componentDidMount() {
    if (this.checkbox && this.props.focused) {
      this.checkbox.focus();
    }
  }

  onBlur = event => {
    this.setState({ isFocused: false });
    return this.props.onBlur && this.props.onBlur(event);
  };

  onFocus = event => {
    this.setState({ isFocused: true });
    return this.props.onFocus && this.props.onFocus(event);
  };

  render() {
    const {
      accessibilityLabel,
      checked,
      describedBy,
      disabled,
      id,
      hasError,
      indeterminate,
      name,
      required,
      value,
      children,
      testID,
      focused,
      isRtl,
      ...rest
    } = this.props;

    return (
      <ThemeConsumer>
        {theme => (
          <label
            htmlFor={id}
            css={getContainerStyles({ ...this.props, theme })}
          >
            <div css={getCheckboxContainerStyles()}>
              <input
                {...rest}
                ref={c => {
                  this.checkbox = c;
                }}
                css={getInputStyles(disabled)}
                aria-checked={indeterminate ? 'mixed' : checked}
                aria-label={accessibilityLabel}
                aria-invalid={hasError}
                aria-describedby={describedBy}
                id={id}
                name={name}
                role="checkbox"
                type="checkbox"
                value={value}
                onBlur={this.onBlur}
                onFocus={this.onFocus}
                checked={checked}
                disabled={disabled}
                {...(required ? { required: true } : {})}
                {...injectTestId(testID)}
              />
              {this.renderCheckbox(theme)}
            </div>
            {this.renderLabelContent(theme)}
          </label>
        )}
      </ThemeConsumer>
    );
  }

  renderLabelContent = theme => {
    if (!this.props.children) return '';
    return (
      <span css={getContentStyles({ ...this.props, theme })}>
        {this.props.children}
      </span>
    );
  };

  renderCheckbox = theme => (
    <div css={getOverlayStyles({ ...this.props, ...this.state, theme })}>
      {this.props.checked && this.renderCheckmark(theme)}
      {!this.props.checked &&
        this.props.indeterminate &&
        this.renderIndeterminateMark(theme)}
    </div>
  );

  renderCheckmark = theme => (
    <svg
      css={getIconStyles({ ...this.props, theme })}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
    >
      <path d="M2.90376758,8.34870587 C2.69875098,8.14368927 2.69875098,7.81127814 2.90376758,7.60624103 L3.64621191,6.86377618 C3.85122852,6.65873907 4.18366016,6.65873907 4.38867676,6.86377618 L6.6875,9.16257892 L11.6113232,4.23877618 C11.8163398,4.03375958 12.1487715,4.03375958 12.3537881,4.23877618 L13.0962324,4.98124103 C13.301249,5.18625763 13.301249,5.51866876 13.0962324,5.72370587 L7.05873242,11.7612264 C6.85369531,11.966243 6.52128418,11.966243 6.31626758,11.7612059 L2.90376758,8.34870587 Z" />
    </svg>
  );

  renderIndeterminateMark = theme => (
    <svg
      css={getIndeterminateStyles({ ...this.props, theme })}
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
    >
      <rect width="8" height="2" x="4" y="7" rx=".5" />
    </svg>
  );
}

function getContainerStyles({ isRtl }) {
  return {
    display: 'flex',
    flexDirection: isRtl ? 'row-reverse' : 'row',
    cursor: 'pointer',
    minHeight: '1rem',
    [`padding${isRtl ? 'Left' : 'Right'}`]: '1rem'
  };
}

function getCheckboxContainerStyles() {
  return {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    userSelect: 'none'
  };
}

function getContentStyles({ theme, isRtl, disabled }) {
  return {
    ...carinaFontReset(theme),
    color: disabled ? theme.font.color.disabled : theme.font.color.base,
    fontSize: theme.font.base.size.s,
    textAlign: isRtl ? 'right' : 'left',
    direction: isRtl ? 'rtl' : 'ltr',
    [`margin${isRtl ? 'Right' : 'Left'}`]: '0.5rem'
  };
}

function getInputStyles(disabled) {
  return {
    cursor: disabled ? 'not-allowed' : 'pointer',
    position: 'absolute',
    opacity: 0
  };
}

function getIconStyles({ theme, disabled, isRtl }) {
  const square = `calc(1rem - (${theme.size.border.base}px * 2))`;
  const center = 'translate(-50%, -50%)';
  return {
    position: 'absolute',
    top: '50%',
    [isRtl ? 'right' : 'left']: '50%',
    transform: 'translate(-50%, -50%)',
    width: square,
    height: square,
    fill: disabled ? theme.font.color.disabled : theme.font.color.fill,
    pointerEvents: 'none', // svg can interrupt event bubbling (observed when used with final-form)
    ...(isRtl ? { transform: `scaleX(-1) ${center}` } : {})
  };
}

function getIndeterminateStyles({ theme, disabled }) {
  return {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '1rem',
    height: '1rem',
    fill: disabled ? theme.font.color.disabled : theme.font.color.fill
  };
}

function getOverlayStyles({
  checked,
  indeterminate,
  disabled,
  hasError,
  isFocused,
  theme
}) {
  return {
    ...getBoxStyles(theme, isFocused, hasError),
    ...(checked || indeterminate ? getCheckedStyles(theme, hasError) : {}),
    ...(hasError ? getErrorStyles(theme, hasError) : {}),
    ...(disabled ? getDisabledStyles(theme, checked, indeterminate) : {})
  };
}

function getBoxStyles(theme, isFocused, hasError) {
  // account for border widths
  const square = `calc(1rem - (${theme.size.border.base}px * 2))`;
  const focusRing = `0 0 0 ${theme.size.border.focus}px ${
    hasError
      ? theme.backgroundColor.danger.fill.focus
      : theme.backgroundColor.interactive.fill.focus
  }`;
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.size.border.radius.twoX,
    height: square,
    width: square,
    borderWidth: theme.size.border.base,
    borderStyle: 'solid',
    borderColor: theme.borderColor.base,
    backgroundColor: 'transparent',
    boxShadow: isFocused ? focusRing : 'none'
  };
}

function getCheckedStyles(theme, hasError) {
  return {
    borderColor: theme.borderColor.interactive.base,
    backgroundColor: hasError
      ? theme.backgroundColor.danger.fill.base
      : theme.backgroundColor.interactive.fill.base
  };
}

function getErrorStyles(theme) {
  return {
    borderColor: theme.borderColor.danger.base
  };
}

function getDisabledStyles(theme) {
  return {
    borderColor: theme.borderColor.disabled,
    backgroundColor: theme.borderColor.disabled,
    color: theme.font.color.disabled,
    pointerEvents: 'none'
  };
}
